import { reportImageLoadError } from "@glide/common-core/dist/js/components/image-load-errors";
import type { DropImageHandler } from "@glide/common-core/dist/js/components/portable-renderers";
import { imageSourceURL, isImageRoutingTransition } from "@glide/common-core/dist/js/components/preserved-image-url";
import type { DropData } from "@glide/common-core/dist/js/drop-controller";
import { featureSettingsObservable, getFeatureSettingProbability } from "@glide/common-core/dist/js/feature-settings";
import { isPlayer } from "@glide/common-core/dist/js/routes";
import { maybeFrontendTrace } from "@glide/common-core/dist/js/tracing";
import { getEmoji, logInfo } from "@glide/support";
import { GeneratedImage } from "@glide/generated-image";
import React from "react";

import { Droppable } from "../droppable/droppable";
import { AnimatedImage, EmojiImg, EmojiImgWrapper } from "./img-style";

interface Props extends React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> {
    className?: string;
    src?: string;
    alt?: string;
    alternate?: React.ReactNode;
    onDropImage?: DropImageHandler;
    altBehavior?: "loading" | "error" | "both";
    fadeIn?: boolean;
    isPages?: boolean;
    "data-testid"?: string;
}
interface State {
    loaded: boolean;
    error: boolean;
    src?: string;
    lastSrc?: string;
    retainedSrc?: string;
    imageSlot: 0 | 1;
    suppressFadeIn: boolean;
    loadedReferrerPolicy?: "no-referrer";
    dragOver: boolean;
    transitioning: boolean;
    isPages: boolean;
}

const cache: any = {};

export class Img extends Droppable<Props, State> {
    private _imgRef = React.createRef<HTMLImageElement>();
    private readonly _pendingImageRef = React.createRef<HTMLImageElement>();

    public state: State = {
        loaded: false,
        error: false,
        dragOver: false,
        transitioning: false,
        lastSrc: "",
        isPages: false,
        imageSlot: 0,
        suppressFadeIn: false,
    };

    private image: HTMLImageElement | undefined;

    public constructor(props: Props) {
        super(props);

        this.state.src = props.src === undefined ? undefined : imageSourceURL(props.src);
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        this.state.isPages = props.isPages || false;

        if (this.state.src !== undefined && cache[this.state.src]) {
            this.state.loaded = true;
        }
    }

    public async componentDidMount() {
        featureSettingsObservable.subscribe(this.onFeatureSettingsChanged);
        if (!this.state.loaded) {
            this.loadImage().catch(r => logInfo(r));
        }
    }

    public componentWillUnmount() {
        featureSettingsObservable.unsubscribe(this.onFeatureSettingsChanged);
        this.unloadImage();
    }

    private onFeatureSettingsChanged = () => {
        const src = this.props.src === undefined ? undefined : imageSourceURL(this.props.src);
        if (src !== this.state.src) this.changeImage(src);
    };

    public UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (this.props.src === nextProps.src) return;
        const nextSrc = nextProps.src === undefined ? undefined : imageSourceURL(nextProps.src);

        if (
            this.state.loaded &&
            nextProps.src !== undefined &&
            cache[nextProps.src] &&
            !isImageRoutingTransition(this.state.src, nextSrc)
        ) {
            this.setState({
                src: nextSrc,
                retainedSrc: undefined,
                suppressFadeIn: false,
                loadedReferrerPolicy: undefined,
            });
            return;
        }

        this.changeImage(nextSrc);
    }

    protected onDataDropped(dropData: DropData): void {
        if (isPlayer()) return;
        switch (dropData.kind) {
            case "uri":
                this.setState({
                    src: dropData.uri,
                    loaded: true,
                    error: false,
                    retainedSrc: undefined,
                    suppressFadeIn: false,
                    loadedReferrerPolicy: undefined,
                });
                break;
            case "file":
                const fr = new FileReader();
                fr.onload = (_e: Event) => {
                    const newUrl = fr.result as string;
                    this.setState({
                        src: newUrl,
                        loaded: true,
                        error: false,
                        retainedSrc: undefined,
                        suppressFadeIn: false,
                        loadedReferrerPolicy: undefined,
                    });
                };

                fr.readAsDataURL(dropData.file);
                break;
        }

        super.onDataDropped(dropData);
    }

    private unloadImage() {
        if (this.image === undefined) return;

        try {
            delete (this.image as any).onerror;
            delete (this.image as any).onload;
            delete (this.image as any).src;
        } catch {
            // do nothing
        }

        delete this.image;
        this.image = undefined;
    }

    private onError = () => {
        const src = this.state.src;
        if (src !== undefined) {
            cache[src] = false;
        }
        const image = this.image;
        if (image !== undefined) {
            const fail = () => {
                if (image === this.image && src === this.state.src)
                    this.setState({ error: true, retainedSrc: undefined });
            };
            // Allow the routing refresh to reach every failed image, including ones
            // whose handler already unsubscribed. A missed refresh still shows alternate.
            // A source that already failed before a route change is not a new routing failure.
            const handled = !this.state.error && src !== undefined && reportImageLoadError(src);
            if (handled || this.state.retainedSrc !== undefined) setTimeout(fail, 0);
            else fail();
        }
    };

    private onLoad = () => {
        const src = this.state.src;
        if (src !== undefined) {
            cache[src] = true;
        }
        if (this.image !== undefined) {
            this.setState(state => ({
                loaded: true,
                error: false,
                retainedSrc: undefined,
                imageSlot: state.retainedSrc === undefined ? state.imageSlot : state.imageSlot === 0 ? 1 : 0,
                loadedReferrerPolicy:
                    state.retainedSrc !== undefined && state.src?.startsWith("https://lh3.googleusercontent.com")
                        ? "no-referrer"
                        : undefined,
            }));
        }
    };

    private changeImage(nextSrc: string | undefined) {
        // Changing delivery routes must not remove an already visible image.
        const retain = isImageRoutingTransition(this.state.src, nextSrc);
        const retainedSrc = retain
            ? this.state.retainedSrc ?? (this.state.loaded && !this.state.error ? this.state.src : undefined)
            : undefined;
        this.unloadImage();

        if (retainedSrc !== undefined && nextSrc === retainedSrc) {
            this.setState({ loaded: true, error: false, src: nextSrc, retainedSrc: undefined, transitioning: false });
            return;
        }

        this.setState(
            {
                loaded: false,
                // Keep an existing error placeholder while trying another route for the same image.
                error: retain && this.state.error,
                src: nextSrc,
                retainedSrc,
                suppressFadeIn: retainedSrc !== undefined,
                transitioning: this.state.isPages && !retain,
                lastSrc: this.state.isPages && !retain ? this.state.src : undefined,
            },
            () => {
                const loading = this.loadImage();
                const image = this.image;
                loading
                    .then(() => {
                        if (image === this.image && nextSrc === this.state.src) this.setState({ transitioning: false });
                    })
                    .catch(r => logInfo(r));
            }
        );
    }

    private async loadImage() {
        const src = this.state.src;
        if (src === undefined || src.startsWith("glide:")) return;

        // This is treated as an error on purpose
        if (src.trim() === "") {
            this.onError();
            return;
        }

        // A decoded detached preload can still refetch when another element receives
        // its URL. Keep the old image visible and load the element we will actually show.
        this.image = this.state.retainedSrc === undefined ? new Image() : this._pendingImageRef.current ?? undefined;
        if (this.image === undefined) {
            logInfo("Missing mounted image during routing transition");
            return;
        }
        if (src.startsWith("https://lh3.googleusercontent.com")) {
            this.image.referrerPolicy = "no-referrer";
        }
        const i = this.image;
        if (this.state.retainedSrc === undefined) i.src = src;
        if (i.decode === undefined) {
            i.onload = () => {
                if (i === this.image && src === this.state.src) this.onLoad();
            };
            i.onerror = () => {
                if (i === this.image && src === this.state.src) this.onError();
            };
        } else {
            try {
                await maybeFrontendTrace(
                    "imageLoad",
                    undefined,
                    getFeatureSettingProbability("traceImageLoadProbability"),
                    async fields => {
                        await i.decode();
                        fields.width = i.naturalWidth;
                        fields.height = i.naturalHeight;
                    }
                );
                // `loadImage` is reentrant, so this might not be true anymore
                if (i === this.image && src === this.state.src) {
                    this.onLoad();
                }
            } catch (err: unknown) {
                if (i === this.image && src === this.state.src) {
                    // Fun story, safari isn't spec compliant. I know big shocker! But if you read:
                    // https://html.spec.whatwg.org/multipage/embedded-content.html#dom-img-decode
                    // You will find that a browser should throw EncodingError if it can't decode an
                    // an image. Unfortunately Safari just does this any time it sees an SVG for...
                    // reasons? Anyway it can load the SVG, it's just a dumb browser soooooo...
                    if (src.includes(".svg") && /^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
                        this.onLoad();
                    } else {
                        this.onError();
                    }
                }
            }
        }
    }

    public render(): React.ReactNode {
        const { className, altBehavior, src: propSrc, alt, onDropImage, fadeIn, isPages: _, ...rest } = this.props;
        const { src, lastSrc, retainedSrc, transitioning, loaded, error, imageSlot, suppressFadeIn } = this.state;

        const droppableProps = this.getDroppableProps();
        let style = {
            ...rest.style,
            ...this.getDroppableStyle(),
        };
        style.display = "block";

        // This is purely here to get rid of a react warning because of a nasty
        // interaction between TS and styled components.
        const { isOverlayOpened, ...realRest } = rest as any;

        const emoji = propSrc === undefined ? undefined : getEmoji(propSrc);
        if (emoji !== undefined) {
            return (
                <EmojiImgWrapper {...droppableProps} style={style} className={className} {...realRest}>
                    <EmojiImg>{emoji}</EmojiImg>
                </EmojiImgWrapper>
            );
        }

        if (propSrc?.startsWith("glide:")) {
            const [mode, hash] = propSrc.substr("glide:".length).split(",", 2);
            return <GeneratedImage hash={hash} mode={mode} className={className} {...droppableProps} style={style} />;
        }

        if (transitioning && retainedSrc === undefined) {
            return (
                <>
                    <AnimatedImage
                        key={imageSlot}
                        {...droppableProps}
                        style={style}
                        src={lastSrc}
                        alt={`Loading: ${alt}`}
                        className={className}
                        {...realRest}
                        referrerPolicy={this.state.loadedReferrerPolicy ?? rest.referrerPolicy}
                        fadeIn={fadeIn && !suppressFadeIn}
                        ref={this._imgRef}
                    />
                </>
            );
        }

        if (retainedSrc !== undefined || (loaded && !error)) {
            style.backgroundColor = "transparent";
            return (
                <>
                    <AnimatedImage
                        key={imageSlot}
                        {...droppableProps}
                        style={style}
                        src={retainedSrc ?? src}
                        alt={alt}
                        className={className}
                        {...realRest}
                        referrerPolicy={this.state.loadedReferrerPolicy ?? rest.referrerPolicy}
                        fadeIn={fadeIn && !suppressFadeIn}
                        ref={this._imgRef}
                    />
                    {retainedSrc !== undefined && (
                        <AnimatedImage
                            key={imageSlot === 0 ? 1 : 0}
                            crossOrigin={rest.crossOrigin}
                            referrerPolicy={
                                src?.startsWith("https://lh3.googleusercontent.com")
                                    ? "no-referrer"
                                    : rest.referrerPolicy
                            }
                            src={src}
                            alt=""
                            aria-hidden={true}
                            style={{ display: "none" }}
                            fadeIn={false}
                            ref={this._pendingImageRef}
                        />
                    )}
                </>
            );
        }

        const alternate = this.props.alternate;
        // const showAlt = alternate !== undefined && this.state.error;
        let showAlt: boolean = (src ?? "").trim().length === 0;
        if (alternate !== undefined && !showAlt) {
            switch (altBehavior ?? "error") {
                case "error":
                    showAlt = this.state.error;
                    break;
                case "loading":
                    showAlt = !this.state.error;
                    break;
                case "both":
                    showAlt = true;
                    break;
            }
        }

        if (showAlt && this.props.alternate !== undefined) {
            style = {
                ...style,
                backgroundColor: "transparent",
            };
        }

        return (
            <div {...droppableProps} className={className} style={style} {...realRest}>
                {showAlt && this.props.alternate}
            </div>
        );
    }
}
